/**
 * AdvanceLedgerPage
 * -----------------
 * Standalone Dr/Cr ledger view for either:
 *   - A single advance reference  (URL: /advance-ledger?ref=<REF>)
 *   - All advances of one customer (URL: /advance-ledger?customer=<NAME>)
 *
 * Designed to be opened in a NEW TAB from the Advance tab's table-row
 * action buttons so finance can keep their reconciliation context open.
 *
 * Supports:
 *   - Print (browser native, custom print stylesheet)
 *   - Export to Excel (XLSX via SheetJS)
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Printer, FileSpreadsheet, ArrowLeft, BookOpen, Receipt } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function AdvanceLedgerPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ref = params.get("ref") || "";
  const customer = params.get("customer") || "";
  const customerParam = params.get("customer") || "";
  const amountParam = params.get("amount") || "";
  const partnerCodeParam = params.get("partner_code") || "";
  const uploadIdParam = params.get("upload_id") || "";
  // `customer` query is used in two modes:
  //   - when ref present → disambiguates which advance to fetch
  //   - when ref absent  → triggers customer-book mode
  const mode = ref ? "ref" : "customer";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setErr(null);
      try {
        const token = localStorage.getItem("token");
        let url;
        if (mode === "ref") {
          const qs = new URLSearchParams();
          if (customerParam) qs.append("customer", customerParam);
          if (amountParam) qs.append("amount", amountParam);
          if (partnerCodeParam) qs.append("partner_code", partnerCodeParam);
          if (uploadIdParam) qs.append("upload_id", uploadIdParam);
          const tail = qs.toString() ? `?${qs.toString()}` : "";
          url = `${BACKEND_URL}/api/advance-payments/${encodeURIComponent(ref)}${tail}`;
        } else {
          url = `${BACKEND_URL}/api/advance-payments-by-customer/${encodeURIComponent(customer)}`;
        }
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (ref || customer) load();
    return () => { cancelled = true; };
  }, [ref, customer, mode, customerParam, amountParam, partnerCodeParam, uploadIdParam]);

  // Normalize the API shape into a single render-friendly object.
  const view = useMemo(() => {
    if (!data) return null;
    if (mode === "ref") {
      // Single-ref endpoint returns { ref, customer, amount, utilized, balance, status, mode, ledger }
      const totalCredit = (data.ledger || []).reduce((s, r) => s + (r.credit || 0), 0);
      const totalDebit = (data.ledger || []).reduce((s, r) => s + (r.debit || 0), 0);
      return {
        title: `Advance Ledger — ${data.ref}`,
        subtitle: `Customer: ${data.customer || "-"} · Partner: ${data.partner_code || "-"} · Mode: ${data.mode || "-"}`,
        ledger: data.ledger || [],
        summary: {
          total_received: data.amount || totalCredit,
          total_utilized: data.utilized || totalDebit,
          balance: data.balance ?? totalCredit - totalDebit,
        },
        status: data.status,
        meta: {
          ref: data.ref,
          customer: data.customer,
          partner_code: data.partner_code,
          mode: data.mode,
        },
      };
    }
    // Customer endpoint returns { customer, ledger, summary, advance_refs, partner_codes }
    return {
      title: `Customer Advance Ledger Book — ${data.customer}`,
      subtitle: `${data.advance_count || 0} advance reference(s): ${(data.advance_refs || []).join(", ")} · Partners: ${(data.partner_codes || []).join(", ")}`,
      ledger: data.ledger || [],
      summary: data.summary || {},
      status: (data.summary?.balance > 0
        ? "credit_remaining"
        : data.summary?.balance < 0
          ? "over_utilized"
          : "fully_utilized"),
      meta: {
        customer: data.customer,
        advance_refs: data.advance_refs,
        partner_codes: data.partner_codes,
      },
    };
  }, [data, mode]);

  const onPrint = () => window.print();

  const onExportExcel = () => {
    if (!view) return;
    const rows = view.ledger.map((r, idx) => ({
      "#": idx + 1,
      Date: r.date || "",
      Reference: r.advance_ref || view.meta.ref || "",
      Description: r.description || "",
      Tracking: r.tracking || "",
      Debit: r.debit || 0,
      Credit: r.credit || 0,
      Balance: r.balance || 0,
      Type: r.type || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    // Summary block
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [],
        ["Summary"],
        ["Total Received (Cr)", view.summary.total_received || 0],
        ["Total Utilized (Dr)", view.summary.total_utilized || 0],
        ["Balance", view.summary.balance || 0],
      ],
      { origin: -1 },
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ledger");
    const fname =
      mode === "ref"
        ? `Advance_${view.meta.ref || "ref"}_ledger.xlsx`
        : `Customer_${(view.meta.customer || "customer").replace(/[^a-z0-9]+/gi, "_")}_advance_book.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success(`Exported ${rows.length} row(s)`);
  };

  if (!ref && !customer) {
    return (
      <div className="p-6 text-center text-slate-600">
        Missing query parameter — open this page with <code>?ref=...</code> or <code>?customer=...</code>.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="advance-ledger-page">
      {/* Print stylesheet hides everything outside the printable area */}
      <style>{`
        @media print {
          body { background: white; }
          .no-print { display: none !important; }
          .print-card { box-shadow: none !important; border: none !important; }
          table { font-size: 11px; }
          @page { size: A4; margin: 12mm 10mm; }
        }
      `}</style>

      {/* Toolbar (hidden in print) */}
      <div className="flex flex-wrap items-center gap-2 mb-4 no-print">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="adv-ledger-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="outline" size="sm" onClick={onPrint} data-testid="adv-ledger-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print / PDF
          </Button>
          <Button variant="outline" size="sm" onClick={onExportExcel} data-testid="adv-ledger-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Export Excel
          </Button>
        </div>
      </div>

      <Card className="print-card" data-testid="adv-ledger-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            {mode === "ref" ? <Receipt className="w-5 h-5 text-blue-600" /> : <BookOpen className="w-5 h-5 text-violet-600" />}
            {view?.title || "Loading…"}
          </CardTitle>
          {view?.subtitle && (
            <div className="text-xs text-slate-600 mt-1">{view.subtitle}</div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading ledger…
            </div>
          ) : err ? (
            <div className="text-red-600 text-sm">Error: {err}</div>
          ) : view ? (
            <>
              {/* Summary tiles */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                  <div className="text-[10px] text-emerald-700 uppercase">Total Credit</div>
                  <div className="text-sm sm:text-base font-bold text-emerald-900">₹{fmt(view.summary.total_received)}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-2 text-center">
                  <div className="text-[10px] text-red-700 uppercase">Total Debit</div>
                  <div className="text-sm sm:text-base font-bold text-red-900">₹{fmt(view.summary.total_utilized)}</div>
                </div>
                <div className={`border rounded p-2 text-center ${(view.summary.balance || 0) >= 0 ? "bg-blue-50 border-blue-200" : "bg-amber-50 border-amber-200"}`}>
                  <div className="text-[10px] uppercase text-slate-700">Balance</div>
                  <div className={`text-sm sm:text-base font-bold ${(view.summary.balance || 0) >= 0 ? "text-blue-900" : "text-amber-900"}`}>
                    ₹{fmt(view.summary.balance)}
                  </div>
                </div>
              </div>

              {view.status && (
                <div className="mb-3">
                  {view.status === "fully_utilized" && <Badge className="bg-emerald-100 text-emerald-800">Fully Utilized</Badge>}
                  {view.status === "credit_remaining" && <Badge className="bg-blue-100 text-blue-800">Credit Remaining</Badge>}
                  {view.status === "over_utilized" && <Badge className="bg-amber-100 text-amber-800">Over Utilized</Badge>}
                </div>
              )}

              <div className="overflow-x-auto border border-slate-200 rounded">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Date</th>
                      {mode === "customer" && <th className="px-2 py-2 text-left">Ref</th>}
                      <th className="px-2 py-2 text-left">Description</th>
                      <th className="px-2 py-2 text-left">Tracking</th>
                      <th className="px-2 py-2 text-right">Debit ₹</th>
                      <th className="px-2 py-2 text-right">Credit ₹</th>
                      <th className="px-2 py-2 text-right">Balance ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.ledger.length === 0 && (
                      <tr><td colSpan={mode === "customer" ? 8 : 7} className="text-center text-slate-500 py-6">No ledger entries</td></tr>
                    )}
                    {view.ledger.map((row, idx) => (
                      <tr
                        key={idx}
                        className={`border-t ${row.type === "advance" ? "bg-emerald-50/40" : "bg-white"}`}
                        data-testid={`adv-ledger-row-${idx}`}
                      >
                        <td className="px-2 py-1.5 text-slate-500">{idx + 1}</td>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{row.date || "-"}</td>
                        {mode === "customer" && <td className="px-2 py-1.5 font-mono text-[11px] text-violet-700">{row.advance_ref || "-"}</td>}
                        <td className="px-2 py-1.5">{row.description || "-"}</td>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{row.tracking || "-"}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-red-700">{row.debit ? fmt(row.debit) : "-"}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{row.credit ? fmt(row.credit) : "-"}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-semibold">{fmt(row.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t-2 border-slate-300 font-semibold">
                    <tr>
                      <td colSpan={mode === "customer" ? 5 : 4} className="px-2 py-2 text-right">Totals</td>
                      <td className="px-2 py-2 text-right font-mono text-red-700">₹{fmt(view.summary.total_utilized)}</td>
                      <td className="px-2 py-2 text-right font-mono text-emerald-700">₹{fmt(view.summary.total_received)}</td>
                      <td className="px-2 py-2 text-right font-mono">₹{fmt(view.summary.balance)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="text-[10px] text-slate-500 mt-3 no-print">
                Generated at {new Date().toLocaleString()}
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
